import { Text } from "@solomon-apps/shared/src/components/ui/text";
import React from "react";

import { formatObservedUsdv } from "@/hooks/positionAmounts";
import { KeyStatsRow } from "@/pages/Dashboard/components/PortfolioKeyStats";
import { type Holding, holdingApyDisplay } from "@/pages/Positions/holdings";

import { formatLongDate } from "../../shared/format";

interface PositionKeyStatsProps {
    holding: Holding;
}

/**
 * Per-holding key-stats row — the same full-bleed `KeyStatsRow` mechanism the
 * Portfolio/Positions pages use, scoped to one holding: USDv Position (counted
 * once) · APY (from paid settlement rewards) · USDv Earned and USDv
 * Pending (summed across the holding's underlying positions). Sits at the top
 * of the position detail, under the back-link + heading.
 */
export function PositionKeyStats({
    holding
}: PositionKeyStatsProps): React.ReactElement {
    const displayAmount = holding.balanceLoading
        ? "..."
        : formatObservedUsdv(holding.balance);
    const lp = holding.kind === "lp" ? holding.positions[0] : null;
    const apy = holdingApyDisplay(holding);
    const observedAt = lp
        ? (lp.observation?.observedAt ?? lp.observedAt ?? null)
        : null;

    return (
        <>
            <KeyStatsRow
                stats={[
                    { label: "USDv Position", value: displayAmount },
                    {
                        label: "APY",
                        value: apy,
                        hint:
                            apy === "—"
                                ? "APY needs paid reward history and a positive qualifying balance from completed settlements in the last 30 days."
                                : "Paid rewards divided by qualifying balance over completed settlement periods in the last 30 days, expressed as an annual rate."
                    },
                    {
                        label: "USDv Earned",
                        value: formatObservedUsdv(holding.rewardsEarned),
                        hint: "Total USDv paid to this holding across its programs. Payments can go to a separate reward destination."
                    },
                    {
                        label: "USDv Pending",
                        value: formatObservedUsdv(holding.rewardsPending),
                        hint:
                            holding.rewardsPending === null
                                ? "Pending rewards are unavailable because the settlement history is incomplete."
                                : "USDv calculated in completed settlements but not yet paid. Rewards from the current period are not included."
                    }
                ]}
            />
            {lp && (
                <Text
                    variant="fine-print"
                    className="pb-4 text-foreground-muted"
                >
                    {holding.surface}, USDv principal only.{" "}
                    {observedAt
                        ? `Observed ${formatLongDate(observedAt)}.`
                        : lp.observation?.fetchedAt
                          ? `Fetched ${formatLongDate(lp.observation.fetchedAt)}. Observation time unavailable.`
                          : "Observation time unavailable."}
                </Text>
            )}
        </>
    );
}
